/**
 * Human-readable display names for TCM (Tenant Configuration Management)
 * resource types.
 *
 * TCM reports drift against lowercase dotted resource types like
 * "microsoft.entra.conditionalaccesspolicy". Accurate, but unreadable in a
 * table or drawer. These helpers map them to friendly labels
 * ("Entra Conditional Access Policy") for display only — the raw string stays
 * the source of truth on the row and is what we filter / key on.
 *
 * The full 249-entry map lives in the generated companion
 * `tcmResourceNames.generated.ts` (built from the TCM permission manifest by
 * `scripts/Generate-TcmResourceDisplayNames.py`). This module adds the
 * workload labels used by the fallback and the lookup function itself.
 *
 * Pure functions only — unit-tested in tcmResourceNames.test.ts.
 */

import { TCM_RESOURCE_DISPLAY_NAMES } from './tcmResourceNames.generated'

/**
 * Workload namespace (2nd dotted segment) → product label. Used only by the
 * fallback for resource types not in the generated map (i.e. a type added to
 * the TCM catalogue without a regen). Kept in sync with the WORKLOAD dict in
 * the generator script.
 */
const WORKLOAD_LABELS: Readonly<Record<string, string>> = {
  entra: 'Entra',
  exchange: 'Exchange',
  intune: 'Intune',
  teams: 'Teams',
  securityandcompliance: 'Purview',
}

/** TCM resource types are always the lowercase dotted "microsoft.<ns>.<leaf>" shape. */
function isTcmResourceType(value: string): boolean {
  return /^microsoft\.[a-z0-9]+\./i.test(value)
}

/**
 * Fallback for a TCM type not present in the generated map. We can't reliably
 * word-split an unknown concatenated leaf, so we don't fake it: strip the
 * "microsoft." prefix, swap the namespace for its product label, and surface
 * the leaf with just its first letter capitalised. Better than the raw dotted
 * string, honest about what we don't know. New catalogue types should be
 * regenerated into the map (the coverage test enforces this) so this path is
 * only ever hit transiently.
 */
function fallbackTcmName(resourceType: string): string {
  const parts = resourceType.split('.')
  // Expected shape: ["microsoft", "<ns>", "<leaf>", ...]
  if (parts.length < 3) return resourceType
  const ns = parts[1].toLowerCase()
  const leaf = parts.slice(2).join(' ')
  const label = WORKLOAD_LABELS[ns] ?? ns.charAt(0).toUpperCase() + ns.slice(1)
  const prettyLeaf = leaf.charAt(0).toUpperCase() + leaf.slice(1)
  return `${label} ${prettyLeaf}`.trim()
}

/**
 * Friendly display name for a TCM resource type. Returns the curated name from
 * the generated map when present, a structured fallback when the type isn't
 * catalogued yet, and the input unchanged if it isn't a TCM-shaped string.
 */
export function formatTcmResourceType(
  resourceType: string | null | undefined,
): string {
  if (!resourceType) return '—'
  const exact = TCM_RESOURCE_DISPLAY_NAMES[resourceType]
  if (exact) return exact
  // Upstream is lowercase today, but be defensive about casing drift.
  const lower = resourceType.toLowerCase()
  const byLower = TCM_RESOURCE_DISPLAY_NAMES[lower]
  if (byLower) return byLower
  if (isTcmResourceType(resourceType)) return fallbackTcmName(lower)
  return resourceType
}

/**
 * Display name for a unified drift row's resource type, source-aware. TCM
 * types are humanised via the map above; DSC's PascalCase components
 * (e.g. "AADConditionalAccessPolicy") are already legible and pass through
 * unchanged. Safe to call on any resource type — non-TCM strings are returned
 * as-is.
 */
export function prettifyResourceType(
  resourceType: string | null | undefined,
): string {
  if (!resourceType) return '—'
  if (isTcmResourceType(resourceType)) return formatTcmResourceType(resourceType)
  return resourceType
}

// DSC PascalCase component prefixes → product label. Narrower than
// workloadMapping's coarse buckets on purpose: the queue badge names the
// product the customer thinks in (SharePoint vs OneDrive are different
// places to them, even though both live in the SharePoint bucket).
const DSC_PREFIX_LABELS: ReadonlyArray<readonly [RegExp, string]> = [
  [/^SPO/i, 'SharePoint'],
  [/^OneDrive/i, 'OneDrive'],
  // OD prefix only when followed by an uppercase char (ODSettings/ODSharing)
  // — same disambiguation rule workloadMapping uses.
  [/^OD[A-Z]/, 'OneDrive'],
  [/^(AAD|EntraID|Identity)/i, 'Entra'],
  [/^(EXO|Exchange)/i, 'Exchange'],
  [/^Teams/i, 'Teams'],
  [/^Intune/i, 'Intune'],
  [/^(Defender|MDO)/i, 'Defender'],
  [/^(SC|Purview)/, 'Purview'],
]

/**
 * Product/workload label for a drift row's resource type — the customer-facing
 * replacement for the internal TCM/DSC source badge in the unified queue
 * (Monitoring UX plan D5: pipeline names never reach customer surfaces).
 * TCM dotted types resolve via their namespace; DSC PascalCase components via
 * prefix. Unknown shapes fall back to "Microsoft 365" rather than leaking a
 * raw token.
 */
export function workloadLabelForResourceType(
  resourceType: string | null | undefined,
): string {
  if (!resourceType) return 'Microsoft 365'
  if (isTcmResourceType(resourceType)) {
    const ns = resourceType.split('.')[1]?.toLowerCase() ?? ''
    return WORKLOAD_LABELS[ns] ?? (ns ? ns.charAt(0).toUpperCase() + ns.slice(1) : 'Microsoft 365')
  }
  for (const [re, label] of DSC_PREFIX_LABELS) {
    if (re.test(resourceType)) return label
  }
  return 'Microsoft 365'
}
